import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Check, ForkKnife } from '@phosphor-icons/react';

const API = '/api';

const mealOptions = [
  {
    id: 'beef',
    name: 'Beef Tenderloin',
    description: 'Herb-crusted beef tenderloin with red wine reduction',
  },
  {
    id: 'chicken',
    name: 'Roasted Chicken',
    description: 'Free-range chicken with citrus and herbs',
  },
  {
    id: 'fish',
    name: 'Grilled Sea Bass',
    description: 'Fresh sea bass with tropical fruit salsa',
  },
  {
    id: 'vegetarian',
    name: 'Garden Risotto',
    description: 'Creamy risotto with seasonal vegetables',
  },
  {
    id: 'vegan',
    name: 'Vegan Bowl',
    description: 'Quinoa bowl with roasted vegetables and tahini',
  },
];

export default function MealsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    if (!selectedMeal) {
      toast.error('Please select a meal option');
      return;
    }

    setIsSubmitting(true);
    try {
      const guestId = `guest-${Date.now()}`;
      const payload = {
        guest_id: guestId,
        guest_name: data.guest_name,
        meal_choice: selectedMeal,
        allergies: data.allergies || null,
        special_requests: data.special_requests || null,
      };

      await axios.post(`${API}/meals`, payload);
      setIsSubmitted(true);
      toast.success('Meal preference submitted successfully!');
    } catch (error) {
      console.error('Meal submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="meal-success" className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#F0EBE3] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-[#B8A88A]" />
          </div>
          <h1 className="font-display text-3xl text-stone-900 mb-4">
            Delicious Choice!
          </h1>
          <p className="font-body text-stone-600 mb-8">
            Your meal preference has been saved. We can't wait to share this special dinner with you!
          </p>
          <a
            href="/"
            className="inline-block bg-[#C9B99A] text-white px-8 py-3 font-body font-medium
                       transition-all duration-300 hover:bg-[#B8A88A]"
          >
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="meals-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
            Wedding Reception Dinner
          </p>
          <h1 className="page-title">Meal Selection</h1>
          <p className="page-subtitle">
            Choose your entrée for the reception dinner
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Guest Name */}
            <div className="space-y-2">
              <Label htmlFor="guest_name" className="font-body text-sm text-stone-700">
                Your Full Name *
              </Label>
              <Input
                id="guest_name"
                data-testid="input-guest-name"
                {...register('guest_name', { required: 'Name is required' })}
                className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                placeholder="Enter your name"
              />
              {errors.guest_name && (
                <p className="text-red-500 text-sm">{errors.guest_name.message}</p>
              )}
            </div>

            {/* Meal Options */}
            <div className="space-y-4">
              <Label className="font-body text-sm text-stone-700">
                Select Your Entrée *
              </Label>
              <div className="grid grid-cols-1 gap-4">
                {mealOptions.map((meal) => (
                  <button
                    key={meal.id}
                    type="button"
                    data-testid={`meal-option-${meal.id}`}
                    onClick={() => setSelectedMeal(meal.id)}
                    className={`p-6 text-left border transition-all duration-200
                      ${selectedMeal === meal.id
                        ? 'border-[#C9B99A] bg-[#FAF8F5]'
                        : 'border-stone-200 bg-white hover:border-[#C9B99A]'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-display text-lg text-stone-900">
                          {meal.name}
                        </h3>
                        <p className="font-body text-sm text-stone-500 mt-1">
                          {meal.description}
                        </p>
                      </div>
                      {selectedMeal === meal.id && (
                        <div className="w-6 h-6 bg-[#C9B99A] rounded-full flex items-center justify-center">
                          <Check size={14} weight="bold" className="text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Allergies */}
            <div className="space-y-2">
              <Label htmlFor="allergies" className="font-body text-sm text-stone-700">
                Food Allergies
              </Label>
              <Input
                id="allergies"
                data-testid="input-allergies"
                {...register('allergies')}
                className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                placeholder="e.g., Nuts, Shellfish, Dairy"
              />
            </div>

            {/* Special Requests */}
            <div className="space-y-2">
              <Label htmlFor="special_requests" className="font-body text-sm text-stone-700">
                Special Dietary Requests (Optional)
              </Label>
              <Textarea
                id="special_requests"
                data-testid="input-special-requests"
                {...register('special_requests')}
                className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A] min-h-[100px]"
                placeholder="Any other dietary needs or preferences..."
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              data-testid="submit-meal-btn"
              disabled={isSubmitting}
              className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white py-6 font-body font-medium tracking-wide"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <ForkKnife size={20} weight="fill" />
                  Submit Selection
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
